import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { identifyUser } from "../analytics";
import API from "../config/api";

const UserContext = createContext(null);

const DEFAULT_USER = {
  name: "Jules",
  role: "super_admin",
  plan: "enterprise",
  companyName: "Ecovanta",
  companyPlan: "enterprise"
};

function normalizeUser(user) {
  if (!user) return DEFAULT_USER;

  const companyPlan =
    user.company?.plan ||
    user.subscription?.planKey ||
    user.plan ||
    user.subscriptionPlan ||
    DEFAULT_USER.plan;

  return {
    ...user,

    name:
      user.name ||
      user.fullName ||
      user.email ||
      DEFAULT_USER.name,

    role: user.role || DEFAULT_USER.role,

    plan: companyPlan,

    companyPlan,

    userPlan:
      user.plan ||
      user.subscriptionPlan ||
      "",

    companyName:
      user.companyName ||
      user.company?.name ||
      DEFAULT_USER.companyName,

    subscriptionStatus:
      user.company?.subscriptionStatus ||
      user.subscriptionStatus ||
      user.subscription?.status ||
      ""
  };
}

export function UserProvider({ children, user: initialUser }) {
  const [user, setUser] = useState(
    normalizeUser(initialUser || DEFAULT_USER)
  );

  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    async function loadUser() {
      try {
        const token = localStorage.getItem("token") || "";

        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
          headers: token
            ? {
                Authorization: `Bearer ${token}`
              }
            : {}
        });

        if (!response.ok) {
          setLoadingUser(false);
          return;
        }

        const data = await response.json();
        const normalizedUser = normalizeUser(data);

        setUser(normalizedUser);
        identifyUser(normalizedUser);
      } catch (error) {
        console.error("UserContext load failed:", error);
      } finally {
        setLoadingUser(false);
      }
    }

    loadUser();
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      loadingUser,
      isAuthenticated: Boolean(user),
      plan: user?.companyPlan || user?.plan || DEFAULT_USER.plan,
      role: user?.role || DEFAULT_USER.role,
      companyName: user?.companyName || DEFAULT_USER.companyName
    }),
    [user, loadingUser]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);

  if (!context) {
    return {
      user: DEFAULT_USER,
      setUser: () => {},
      loadingUser: false,
      isAuthenticated: true,
      plan: DEFAULT_USER.plan,
      role: DEFAULT_USER.role,
      companyName: DEFAULT_USER.companyName
    };
  }

  return context;
}